import { platformNameConverter, getScoreSections } from '../common-utilities';
import useSWR from 'swr';
import type {
  IAppDetailsAppData,
  IDynamicContent,
  IPlatform,
} from '../common-types';
import { ScoreDetailsView } from '../ScoreDetails';

export type DtacScoreDetailsProps = IPlatform & {
  app: IAppDetailsAppData;
  locale: string;
  eventHandlers: {
    handleScoreTabChange: (tabName: string) => void;
  };
  dynamicContentApiUrl: string;
  translatedText: {
    platformScoreDetailsHeading: string;
    platformScoreDetailsMissingDynamicContent: string;
  };
};

/**
 * DtacScoreDetails from [MicroLibraryAppDetailsUI](https://dev.azure.com/orcha/MicroLibraryTemplate/_git/MicroLibraryAppDetailsUI)
 * @param locale is required, default to "en-GB" if not provided
 * @test ting
 * @info DtacScoreDetails is making a fetch request on the provided dynamicContentApiUrl
 * @param translatedText is required for the ScoreDetailsView component, this is the text that as already been translated by the consumer of this component.
 */

export const DtacScoreDetails = ({
  platformName,
  app,
  locale = 'en-GB',
  eventHandlers,
  dynamicContentApiUrl,
  translatedText,
  ...props
}: DtacScoreDetailsProps) => {
  const { platforms } = app ?? {};
  const relevantPlatform = platforms?.find(
    (p) => platformName === platformNameConverter(p.platformName)
  );

  const reviewId = relevantPlatform?.reviewId;
  const dtacReviewId = relevantPlatform?.dtacData?.reviewId;

  const localeQuery = locale ? `&language=${locale}` : '';

  const fetcher = (url: string) => fetch(url).then((res) => res.json());

  const { data: dynamicContent, isLoading } = useSWR(
    dynamicContentApiUrl && reviewId && localeQuery
      ? `${dynamicContentApiUrl}?reviewId=${reviewId}${localeQuery}`
      : null,
    fetcher
  );

  const dynamicContentData: IDynamicContent | undefined =
    dynamicContent?.dynamicContent;

  const { data: dtacData, isLoading: isDtacLoading } = useSWR(
    dynamicContentApiUrl && dtacReviewId && localeQuery
      ? `${dynamicContentApiUrl}?reviewId=${dtacReviewId}${localeQuery}`
      : null,
    fetcher
  );

  const dtacDynamicContentData: IDynamicContent | undefined =
    dtacData?.dynamicContent;

  const mainSections =
    getScoreSections(
      relevantPlatform?.scoreSections ?? [],
      dynamicContentData
    ) ?? [];

  const dtacSections =
    getScoreSections(
      relevantPlatform?.dtacData?.scoreSections ?? [],
      dtacDynamicContentData
    ) ?? [];

  const combinedSections =
    dtacSections.length > 0 ? mainSections.concat(dtacSections) : mainSections;

  return (
    <>
      <ScoreDetailsView
        translatedText={translatedText}
        {...props}
        eventHandlers={eventHandlers}
        dynamicContent={combinedSections}
        isLoading={isLoading || isDtacLoading}
      />
    </>
  );
};
